import {setupDragHandlers} from "../../../lib/hooks/use-drag";
import {
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {VisionBoardNode, VisionBoardNodeId} from "../../../cdx-models/utils/extended-fields";
import {NodeBushEntry} from "../useVisionBoardData";
import {getCanvasNodeRefs, registerCanvasNode} from "../vision-board-node-selection-store";
import {adaptToGrid, AwarenessStore, TwoNums, VisionBoardBag} from "../canvas-utils";
import ResizeHandles from "../CanvasNodeResizeHandles";
import {CanvasNodeMenu, DefaultNodeMenuItems} from "../CanvasMenu";
import {css, CSSProps} from "@cdx/ds";
import {canvasNodeStyles} from "../vision-board.css";
import {msNextThemes} from "../../../pages/milestones/Timeline/Timeline.css";
import {UserId} from "../../../cdx-models/User";
import {api} from "../../../lib/api";
import {DSAvatarStack} from "../../../components/DSAvatar/DSAvatar";

const OtherSelections = ({userIds}: {userIds: Set<UserId>}) => {
  const users = [...userIds].map((id) => api.getModel({modelName: "user", id})!);
  return (
    <DSAvatarStack
      maxCount={4}
      users={users}
      size={16}
      className={css(
        {position: "absolute", bottom: "100%", right: "4px"},
        canvasNodeStyles.otherAvatar
      )}
    />
  );
};

const useNodeDrag = ({
  bag,
  nodeRef,
  id,
  setIsDragging,
}: {
  nodeRef: MutableRefObject<HTMLDivElement | null>;
  id: VisionBoardNodeId;
  bag: VisionBoardBag;
  setIsDragging: (isDragging: boolean) => void;
}) => {
  const {posRef, store} = bag;
  const [{handlers, unsubs}] = useState(() =>
    setupDragHandlers(
      (startPos, nodeIds: VisionBoardNodeId[]) => {
        let lastDiff = [0, 0] as [number, number];
        const nodeRefs = getCanvasNodeRefs(nodeIds);
        setIsDragging(true);
        return {
          onMove: ([x, y]) => {
            const _mx = x - startPos[0];
            const _my = y - startPos[1];
            const [dx, dy] = [_mx / posRef.current.scale, _my / posRef.current.scale];
            lastDiff = [dx, dy];
            for (const [nodeId, ref] of nodeRefs) {
              const el = ref.current!;
              const node = store.getNode(nodeId);
              if (!node) continue;
              const [posX, posY] = [node.minX, node.minY];
              const [snapX, snapY] = adaptToGrid([posX + dx, posY + dy], store);
              const [relX, relY] = [snapX - posX, snapY - posY];
              el.style.transform = `translate(${relX}px, ${relY}px)`;
              store.emitNodeMoveListener(nodeId, {
                type: "move",
                value: [relX, relY],
                initial: node,
              });
            }
          },
          onStop: (isCancelled) => {
            for (const [nodeId, ref] of nodeRefs) {
              const el = ref.current!;
              el.style.transform = "";
              const node = store.getNode(nodeId);
              if (!node) continue;
              store.emitNodeMoveListener(nodeId, {type: "move", value: [0, 0], initial: node});
            }
            nodeRef.current!.style.cursor = "grab";
            if (!isCancelled && (lastDiff[0] !== 0 || lastDiff[1] !== 0)) {
              for (const nodeId of nodeIds) {
                const selectedBushNode = store.getNode(nodeId);
                if (!selectedBushNode) continue;
                const nodePos: VisionBoardNode["pos"] = selectedBushNode.node.get("pos");
                const [nextX, nextY] = adaptToGrid(
                  [nodePos.x + lastDiff[0], nodePos.y + lastDiff[1]],
                  store
                );
                selectedBushNode.node.set("pos", {
                  x: nextX,
                  y: nextY,
                  w: nodePos.w,
                  h: nodePos.h,
                  parent: nodePos.parent,
                });
              }
            }
            setIsDragging(false);
          },
        };
      },
      () => {
        const currEl = nodeRef.current!;
        currEl.style.cursor = "grabbing";
        const getNodeIds = () => {
          const selectedIdSet = bag.selectionStore.getSelectedNodeIdsSnapshot();
          if (!selectedIdSet.has(id)) {
            // will also be selected, but selection happens within handleClick handler
            return [id];
          } else {
            return [...selectedIdSet];
          }
        };
        const nodeIds = getNodeIds();
        return {
          onDone: () => nodeIds,
          onCancel: () => {
            currEl.style.cursor = "grab";
          },
        };
      }
    )
  );
  useEffect(() => {
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [unsubs]);

  return handlers;
};

const useIsSelectedByOthers = (id: VisionBoardNodeId, awarenessStore: AwarenessStore) => {
  const [otherIds, setOtherIds] = useState<null | Set<UserId>>(null);
  useEffect(() => {
    awarenessStore.registerSelectionListener((selMap) => {
      const ids = selMap.get(id);
      if (!ids) {
        setOtherIds(null);
      } else {
        setOtherIds((prev) => {
          if (prev?.size === ids.size) return prev;
          return ids;
        });
      }
    });
  }, [awarenessStore, id]);
  return otherIds;
};

export const useCanvasNode = ({
  node: bushNode,
  nodeData,
  bag,
  forceAspect,
  disableResize,
  menuItems,
  hasColorBg,
  minDims,
  noBorder,
  onDelete,
  menuOffset,
}: {
  node: NodeBushEntry;
  nodeData: VisionBoardNode;
  bag: VisionBoardBag;
  forceAspect?: number | undefined;
  disableResize?: boolean;
  menuItems?: ReactNode;
  hasColorBg?: boolean;
  minDims: TwoNums;
  noBorder?: boolean;
  onDelete?: (data: VisionBoardNode) => void;
  menuOffset?: CSSProps["pb"];
}) => {
  const {id} = bushNode;
  const {pos, color} = nodeData;
  const [isDragging, setIsDragging] = useState(false);

  const nodeRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    return registerCanvasNode(id, nodeRef);
  }, [id]);

  const handlers = useNodeDrag({bag, nodeRef, id, setIsDragging});

  const selectedByOthers = useIsSelectedByOthers(id, bag.awarenessStore);

  const isSelected = bag.selectionStore.useSelectedNodes((idSet) => idSet.has(id));
  const isActive = bag.selectionStore.useSelectedNodes(
    (idSet) => isSelected && idSet.size === 1 && !bag.isSelecting && !isDragging
  );

  const isPartOfMultiSelect = isSelected && !isActive;

  const handleClick: MouseEventHandler = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (isSelected) {
        bag.selectionStore.removeNodeId(id);
      } else {
        bag.selectionStore.addNodeId(id);
      }
    } else {
      if (!isSelected) {
        bag.selectionStore.setNodeIds([id]);
      } else if (bag.selectionStore.getSelectedNodeIdsSnapshot().size > 1) {
        bag.selectionStore.setNodeIds([id]);
      }
    }
  };

  return {
    isActive,
    nodeRef,
    containerProps: {
      ...handlers,
      onClick: handleClick,
      style: {
        left: pos.x,
        top: pos.y,
        height: pos.h,
        width: pos.w,
      },
      className: css(
        {
          position: "absolute",
          cursor: "grab",
          userSelect: "none",
          zIndex: isSelected ? 2 : undefined,
        },
        !noBorder && canvasNodeStyles.container.base,
        color
          ? hasColorBg
            ? canvasNodeStyles.container.withCustomColorBg
            : canvasNodeStyles.container.withCustomColor
          : canvasNodeStyles.container.noCustomColor,
        color && msNextThemes[color],
        isSelected
          ? color
            ? hasColorBg
              ? canvasNodeStyles.container.selectedWithCustomColorBg
              : canvasNodeStyles.container.selectedWithCustomColor
            : canvasNodeStyles.container.selected
          : selectedByOthers
            ? canvasNodeStyles.container.selectedByOthers
            : null
      ),
    },
    elements: (
      <>
        {selectedByOthers && <OtherSelections userIds={selectedByOthers} />}
        {!isPartOfMultiSelect && (
          <ResizeHandles
            containerRef={nodeRef}
            orgPos={pos}
            bushNode={bushNode}
            bag={bag}
            forceAspect={forceAspect}
            disableResize={disableResize}
            minDims={minDims}
          />
        )}
        {isActive && (
          <CanvasNodeMenu menuOffset={menuOffset}>
            {menuItems}
            <DefaultNodeMenuItems
              nodeData={nodeData}
              node={bushNode}
              bag={bag}
              onDelete={onDelete}
            />
          </CanvasNodeMenu>
        )}
      </>
    ),
  };
};
